'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/lib/context/AuthContext';
import { generateRoomId } from '@/lib/client-utils';
import { PRIMARY_NAV, isNavActive } from './shellNav';
import { ADMIN_NAV } from './shellNav';
import { ProfileDropdown } from './ProfileDropdown';
import { StatusPill } from './StatusPill';
import { PlusIcon, ChevronDownIcon } from './Icons';
import { Brand } from '@/components/ui';

export function TopNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();
  const isHost = user?.role === 'host' || user?.role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  // FIX: Avatar URL bisa datang dari beberapa field response API
  const avatarUrl = (user as any)?.avatar || (user as any)?.photo_url || (user as any)?.profile_picture || (user as any)?.image;

  const initials = useMemo(() => {
    if (!user?.name) return 'GU';
    return user.name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  }, [user?.name]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatarUrl]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // Parity dgn HomeContent "Start instant meeting"
  const startInstantMeeting = () => router.push(`/rooms/${generateRoomId()}?create=true`);

  return (
    <header className="mx-topnav">
      <div className="mx-topnav__brand">
        <Brand size="sm" />
      </div>

      <nav className="mx-topnav__nav" aria-label="Navigasi utama">
        {PRIMARY_NAV.map(({ href, label, Icon }) => {
          const active = isNavActive(pathname, href);
          return (
            <Link
              key={href}
              href={href}
              className={`mx-topnav__item${active ? ' mx-topnav__item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={17} color="currentColor" />
              {label}
            </Link>
          );
        })}
        {user?.role === 'admin' && ADMIN_NAV.map(({ href, label, Icon }) => {
          const active = isNavActive(pathname, href);
          return (
            <Link
              key={href}
              href={href}
              className={`mx-topnav__item${active ? ' mx-topnav__item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={17} color="currentColor" />
              {label}
            </Link>
          );
        })}
      </nav>

      <div className="mx-topnav__right">
        {isHost && (
          <button type="button" className="mx-btn mx-btn--primary mx-btn--sm" onClick={startInstantMeeting} aria-label="Rapat baru">
            <PlusIcon size={15} color="#fff" />
            <span className="mx-topnav__newlabel">Rapat baru</span>
          </button>
        )}

        <StatusPill />

        <div className="mx-topnav__user" ref={userRef}>
          <button
            type="button"
            className="mx-topnav__avatar"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Menu profil ${user?.name || ''}`.trim()}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {avatarUrl && !avatarFailed ? (
              // FIX: Foto profil bulat, bukan gepeng
              <img
                src={avatarUrl}
                alt={user?.name || 'Profil'}
                onError={() => setAvatarFailed(true)}
                style={{ width: 34, height: 34, aspectRatio: '1 / 1', borderRadius: '50%', objectFit: 'cover', display: 'block', flexShrink: 0 }}
              />
            ) : (
              <span className="mx-avatar mx-avatar--sm" style={{ background: '#0ea5e9' }}>{initials}</span>
            )}
            <ChevronDownIcon size={15} color="currentColor" />
          </button>
          {menuOpen ? <ProfileDropdown onNavigate={() => setMenuOpen(false)} /> : null}
        </div>
      </div>
    </header>
  );
}
